import { types, getParent, type Instance } from 'mobx-state-tree';
import type { IMetaModel } from '../model/metamodel';
import type { IMultiplePropagationConstraint } from '../model/metamodel/multiple_propagation_constraint';

/** The EDG tools (the `activeTool` enumeration). */
type EDGTool =
  | 'select'
  | 'add_object'
  | 'add_dependency'
  | 'add_inheritance'
  | 'show_bul'
  | 'show_tdl';
type DependencyType = 'OPTIONAL_N' | 'OPTIONAL_1' | 'MANDATORY_N' | 'MANDATORY_1';

/** A newly-added MPC change set, as held by the object inspector's MPC inspector. */
interface IAddedMPC {
  readonly id: string;
  readonly primaryPath?: string;
  readonly secondaryPath?: string;
}

// The EDG store's parent is the UI store; declare the narrow surface the EDG
// views read from it (avoids an import cycle with the UI store, which embeds EDG).
interface IEDGUIParent {
  readonly highlightedPath: string[];
  readonly selected: string | null;
  readonly selectedMPC: string | null;
  readonly addedMPCs: IAddedMPC[] | null | undefined;
}

// Two levels up is the RootStore. The EDG `selectedMPC` view reaches the active
// model version's metamodel through it; declare just that path.
interface IEDGRoot {
  readonly projects: {
    readonly activeModelVersion:
      | { readonly contentAvailable: boolean; readonly content: { readonly metamodel: IMetaModel } | null }
      | null
      | undefined;
  };
}

// Hand-declared instance interface for the EDG store, so the sibling views
// (`selectedAddedMPC`, `selectedMPC`, `primaryPath`) read each other (§3/§4).
interface IEDG {
  readonly highlightedPath: string[];
  readonly highlighted: string[];
  readonly selectedAddedMPC: IAddedMPC | undefined;
  readonly selectedMPC: IMultiplePropagationConstraint | null | undefined;
  readonly primaryPath: string[];
  readonly secondaryPath: string[];
}

/**
 * UI state for the EDG view.
 */
const EDG = types
  .model('UIEDG', {
    // which tool is active
    activeTool: types.maybeNull(
      types.enumeration([
        'select',
        'add_object',
        'add_dependency',
        'add_inheritance',
        'show_bul',
        'show_tdl',
      ])
    ),
    // for the add_dependency tool: which dependency type is active
    activeDependencyType: types.optional(
      types.enumeration(['OPTIONAL_N', 'OPTIONAL_1', 'MANDATORY_N', 'MANDATORY_1']),
      'OPTIONAL_N'
    ),
  })
  .views((self0) => {
    // Rebind `self` to the hand-declared interface so the sibling views below
    // (`self.selectedAddedMPC`, `self.selectedMPC`, `self.primaryPath`) type
    // (§4). `getParent` still receives the real node `self0`.
    const self = self0 as unknown as IEDG;
    return {
    /**
     * Calculate the ids of the dependencies/inheritances that should be highlighted when a
     * method is selected.
     */
    get highlightedPath() {
      const ui = getParent<IEDGUIParent>(self0);
      return ui.highlightedPath;
    },
    /**
     * Return an array of elements that should be highlighted.
     *
     * Returns an array with the 'selected' property of the parent store or an
     * empty array if that one is falsy.
     */
    get highlighted() {
      const selected = getParent<IEDGUIParent>(self0).selected;
      if (selected) {
        return [selected];
      } else {
        return [];
      }
    },
    // Get the newly added MPC that is currently selected.
    get selectedAddedMPC() {
      const ui = getParent<IEDGUIParent>(self0);
      const selectedMPC = ui.selectedMPC;
      if (ui.addedMPCs != null) {
        return ui.addedMPCs.find((mpc) => mpc.id === selectedMPC);
      }
    },
    // Get the currently selected multiple propagation constraint.
    get selectedMPC() {
      const ui = getParent<IEDGUIParent>(self0);
      const selected = ui.selected;
      const selectedMPC = ui.selectedMPC;
      const root = getParent<IEDGRoot>(self0, 2);
      if (
        selectedMPC &&
        root.projects.activeModelVersion &&
        root.projects.activeModelVersion.contentAvailable
      ) {
        const metamodel = root.projects.activeModelVersion.content!.metamodel;
        // `selected` can be null when no object is selected; the legacy code
        // passes it straight through and relies on a null lookup returning
        // undefined. Cast to satisfy the `string` param (byte-identical).
        const selectedObject = metamodel.getObjectTypeById(selected as string);
        if (selectedObject) {
          return selectedObject.getMultiplePropagationConstraintById(selectedMPC);
        } else {
          return null;
        }
      } else {
        return null;
      }
    },
    // Get the dependencies that are part of the primary path of the currently
    // selected multiple propagation constraint.
    get primaryPath() {
      const selectedAddedMPC = self.selectedAddedMPC;
      const selectedMPC = self.selectedMPC;
      if (selectedAddedMPC != null && selectedAddedMPC.primaryPath != null) {
        // primaryPath is the ids of the elements joined with + => split again
        return selectedAddedMPC.primaryPath.split('+');
      } else if (selectedMPC != null) {
        return selectedMPC.primaryPath.elements.map((d) => d.id);
      } else {
        return [];
      }
    },
    // Get the dependencies that are part of the secondary path of the currently
    // selected multiple propagation constraint. Being part of the primary path
    // takes precedence.
    get secondaryPath() {
      const selectedAddedMPC = self.selectedAddedMPC;
      const selectedMPC = self.selectedMPC;
      if (selectedAddedMPC != null && selectedAddedMPC.secondaryPath != null) {
        // secondaryPath is the ids of the elements joined with + => split again
        return selectedAddedMPC.secondaryPath
          .split('+')
          .filter((id) => !self.primaryPath.includes(id));
      } else if (selectedMPC != null) {
        return selectedMPC.secondaryPath.elements
          .map((d) => d.id)
          .filter((id) => !self.primaryPath.includes(id));
      } else {
        return [];
      }
    },
    };
  })
  .actions((self) => ({
    toggleTool(tool: EDGTool) {
      if (self.activeTool === tool) {
        self.activeTool = null;
      } else {
        self.activeTool = tool;
      }
    },
    activateDependencyType(type: DependencyType) {
      self.activeTool = 'add_dependency';
      self.activeDependencyType = type;
    },
    deactivateTool() {
      self.activeTool = null;
    },
  }));

export type IUIEDG = Instance<typeof EDG>;

export default EDG;
